import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Mail, Globe, Loader2 } from "lucide-react";
import { api, COMPANY } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const initial = { name: "", email: "", phone: "", subject: "General Enquiry", message: "" };
const SUBJECTS = ["General Enquiry", "IP Audit & Valuation", "Trademark Registration", "IP Litigation", "Portfolio Management", "Patents / Copyright / Designs"];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your enquiry has been received. We'll be in touch shortly.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-24 min-h-screen bg-background">
      <div className="container-edge">
        <Reveal>
          <p className="overline mb-4">Contact</p>
          <h1 className="font-display font-light text-5xl md:text-7xl tracking-tight text-primary max-w-3xl mb-16">Let&apos;s protect your ideas.</h1>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Reveal className="lg:col-span-7 border border-border bg-card p-8 md:p-12">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="overline block mb-2">Name *</label>
                  <input data-testid="contact-name" className="field" value={form.name} onChange={set("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className="overline block mb-2">Email *</label>
                  <input data-testid="contact-email" type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="overline block mb-2">Phone</label>
                  <input data-testid="contact-phone" className="field" value={form.phone} onChange={set("phone")} placeholder="+91" />
                </div>
                <div>
                  <label className="overline block mb-2">Subject</label>
                  <select data-testid="contact-subject" className="field" value={form.subject} onChange={set("subject")}>
                    {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Message *</label>
                <textarea data-testid="contact-message" rows={5} className="field resize-none" value={form.message} onChange={set("message")} placeholder="Tell us about your requirement…" />
              </div>
              <button data-testid="contact-submit-button" type="submit" disabled={sending} className="btn-primary w-full md:w-auto disabled:opacity-60">
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send Enquiry"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5 flex flex-col gap-4">
            <div className="border border-border bg-primary text-primary-foreground p-8">
              <p className="overline text-accent mb-6">Head Office</p>
              <ul className="space-y-5">
                <li className="flex gap-3"><MapPin size={20} className="mt-1 shrink-0 text-accent" /><span className="text-lg">{COMPANY.address},<br />{COMPANY.region}</span></li>
                <li className="flex gap-3"><Mail size={20} className="shrink-0 text-accent" /><a href={`mailto:${COMPANY.email}`} className="hover:text-accent transition-colors">{COMPANY.email}</a></li>
                <li className="flex gap-3"><Globe size={20} className="shrink-0 text-accent" /><a href={`https://${COMPANY.website}`} target="_blank" rel="noreferrer" className="hover:text-accent transition-colors">{COMPANY.website}</a></li>
              </ul>
            </div>
            <div className="border border-border flex-1 min-h-[280px] overflow-hidden">
              <iframe data-testid="office-map" title="IPNOMICS Office Location" src={COMPANY.mapEmbed} className="w-full h-full min-h-[280px] grayscale" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
